import { useLocation, useNavigate } from "react-router-dom";
import Icon from "@ant-design/icons";
import { Icons } from "../assets/icons";
import SiderMenu from "../controls/SiderMenu";
import { Children, useEffect, useState } from "react";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

export interface NavigationMenuProps {}

function NavigationMenu(props: NavigationMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [key, setKey] = useState<string | null>();
  const isAppAdmin = useSelector(authorizeSelectors.isAppAdmin);
  const permissions = useSelector(authorizeSelectors.permissions);

  useEffect(() => {
    setKey(location.pathname?.replace(/^\/|\/$/, "")?.trim() ?? null);
  }, [location]);

  function onSelectItem(key: string): void {
    navigate(key, {
      replace: true,
    });
  }

  const items = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: (
        <Icon className="-ml-0.5 text-2xl" component={Icons.MonitoringIcon} />
      ),
    },
    ...(permissions.can("Companies", "view")
      ? [
          {
            key: "companies",
            label: "Companies",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ListAltIcon}
              />
            ),
            children: [
              ...(permissions.can("Companies", "view")
                ? [
                    {
                      key: "companies/grid",
                      label: "Grid",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ViewWeekIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("Companies", "view")
                ? [
                    {
                      key: "companies/tree",
                      label: "Tree",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.SchemaIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("CompanyRoles", "view")
                ? [
                    {
                      key: "companies/company-roles",
                      label: "Roles",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ReduceCapacityIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("PlatformUsers", "view") ||
    permissions.can("CompanyManagers", "view") ||
    permissions.can("CompanyEmployees", "view")
      ? [
          {
            key: "users",
            label: "Users",
            icon: (
              <Icon className="-ml-0.5 text-2xl" component={Icons.GroupIcon} />
            ),
            children: [
              ...(permissions.can("PlatformUsers", "view")
                ? [
                    {
                      key: "users/all",
                      label: "All",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.Groups2Icon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("CompanyManagers", "view")
                ? [
                    {
                      key: "users/managers",
                      label: "Managers",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.SupervisorAccountIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("CompanyEmployees", "view")
                ? [
                    {
                      key: "users/employees",
                      label: "Employees",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.Diversity3Icon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("PlatformUsers", "view")
                ? [
                    {
                      key: "users/admins",
                      label: "Admins",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.SupervisorAccountIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("PlatformConsultants", "view") ||
    permissions.can("PlatformCollaborators", "view") ||
    permissions.can("CounsellingRequests", "view") ||
    permissions.can("Trainings", "view") ||
    permissions.can("WellbeingReports", "view")
      ? [
          {
            key: "affiliates",
            label: "Affiliates",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.HandshakeIcon}
              />
            ),
            children: [
              ...(permissions.can("PlatformConsultants", "view")
                ? [
                    {
                      key: "affiliates/consultants",
                      label: "Consultants ",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.PersonIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("PlatformCollaborators", "view")
                ? [
                    {
                      key: "affiliates/collaborators",
                      label: "Collaborators (Therapists)",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.PartnerExchangeIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("CounsellingRequests", "view")
                ? [
                    {
                      key: "affiliates/counselling-requests",
                      label: "Counselling Requests",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ViewListIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("Trainings", "view")
                ? [
                    {
                      key: "affiliates/trainings",
                      label: "Trainings",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.Groups2Icon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("WellbeingReports", "view")
                ? [
                    {
                      key: "affiliates/reports",
                      label: "Wellbeing Reports",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ReportIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("CompanyPolicies", "view")
      ? [
          {
            key: "policies",
            label: "Policies",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.LisenceIcon}
              />
            ),
            children: [
              {
                key: "policies/all",
                label: "All",
                icon: (
                  <Icon
                    className="-ml-0.5 text-2xl"
                    component={Icons.LisenceIcon}
                  />
                ),
              },
              ...(permissions.can("CompanyPolicyAcknowledgments", "view") || isAppAdmin
                ? [
                    {
                      key: "policies/acknowledgments",
                      label: "Acknowledgments",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.PasskeyIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("CalendarEvents", "view")
      ? [
          {
            key: "calendar",
            label: "Calendar",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.CalendarMonthIcon}
              />
            ),
          },
        ]
      : []),
    // Hide temporarily the Actions Menu Item
    /*...(permissions.can("actions", "view")
      ? [
          {
            key: "actions",
            label: "Actions",
            icon: (
              <Icon className="-ml-0.5 text-2xl" component={Icons.BoltIcon} />
            ),
          },
        ]
      : []),*/
    ...(permissions.can("Rewards", "view") ||
    permissions.can("RewardsAssignment", "view") ||
    permissions.can("RewardRedemptions", "view") ||
    permissions.can("LoyaltyAccrualRules", "view") ||
    isAppAdmin
      ? [
          {
            key: "rewards",
            label: "Rewards",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.SocialLeaderboardIcon}
              />
            ),
            children: [
              ...(permissions.can("Rewards", "view")
                ? [
                    {
                      key: "rewards/grid",
                      label: "Grid",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ViewWeekIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("RewardsAssignment", "view")
                ? [
                    {
                      key: "rewards/assignments",
                      label: "Assignments",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TaskIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("RewardRedemptions", "view")
                ? [
                    {
                      key: "rewards/redemptions",
                      label: "Redemptions",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TaskIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("Rewards", "view")
                ? [
                    {
                      key: "rewards/loyalty-history",
                      label: "History",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ViewWeekIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("LoyaltyAccrualRules", "view") || isAppAdmin
                ? [
                    {
                      key: "rewards/points-rules",
                      label: "Points Rules",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TaskIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("Badges", "view") ||
    permissions.can("BadgesAssignment", "view")
      ? [
          {
            key: "badges",
            label: "Badges",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.AwardStarIcon}
              />
            ),
            children: [
              ...(permissions.can("Badges", "view")
                ? [
                    {
                      key: "badges/grid",
                      label: "Grid",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ViewWeekIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("BadgesAssignment", "view")
                ? [
                    {
                      key: "badges/assigned",
                      label: "Assigned",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TaskIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // Hide Temporary the Test Actions Menu Item
    // ...(permissions.can("test-actions", "view")
    //   ? [
    //       {
    //         key: "test-actions",
    //         label: "Test Actions",
    //         icon: (
    //           <Icon
    //             className="-ml-0.5 text-2xl"
    //             component={Icons.ContractEditIcon}
    //           />
    //         ),
    //       },
    //     ]
    //   : []),
    ...(permissions.can("Bundles", "view") ||
    permissions.can("CompanyAssignments", "view")
      ? [
          {
            key: "bundles",
            label: "Bundles",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.CollectionsBookmarkIcon}
              />
            ),
            children: [
              ...(permissions.can("Bundles", "view")
                ? [
                    {
                      key: "bundles/all",
                      label: "All",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.CollectionsBookmarkIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("Bundles", "view")
                ? [
                    {
                      key: "bundles/assignments",
                      label: "Assignments",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TaskIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("Bundles", "view")
                ? [
                    {
                      key: "bundles/groups",
                      label: "Groups",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.AdGroupIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("Questionnaires", "view") ||
    permissions.can("QuestionnairesAnswers", "view")
      ? [
          {
            key: "tests",
            label: "Tests",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ContractIcon}
              />
            ),
            children: [
              ...(permissions.can("Questionnaires", "view")
                ? [
                    {
                      key: "tests/questionnaires",
                      label: "Questionnaires",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.QuizIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("QuestionnairesAnswers", "view")
                ? [
                    {
                      key: "tests/answers",
                      label: "Answers",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.RateReviewIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      key: "reports",
      label: "Reports",
      icon: <Icon className="-ml-0.5 text-2xl" component={Icons.DocsIcon} />,
      children: [
        {
          key: "reports/all",
          label: "All",
          icon: (
            <Icon className="-ml-0.5 text-2xl" component={Icons.AdGroupIcon} />
          ),
        },
        ...(permissions.can("KPIs", "view")
          ? [
              {
                key: "reports/kpis",
                label: "KPIs",
                icon: (
                  <Icon
                    className="-ml-0.5 text-2xl"
                    component={Icons.AnalyticsIcon}
                  />
                ),
              },
            ]
          : []),
        ...(permissions.can("Metrics", "view")
          ? [
              {
                key: "reports/metrics",
                label: "Organizational Metrics",
                icon: (
                  <Icon
                    className="-ml-0.5 text-2xl"
                    component={Icons.Graph6Icon}
                  />
                ),
              },
            ]
          : []),
        // PD-01 §10 — bundled/consolidated reports.
        {
          key: "reports/bundles",
          label: "Bundle Reports",
          icon: (
            <Icon className="-ml-0.5 text-2xl" component={Icons.ReportIcon} />
          ),
        },
        {
          key: "reports/company",
          label: "Company Reports",
          icon: (
            <Icon className="-ml-0.5 text-2xl" component={Icons.ReportIcon} />
          ),
        },
        {
          key: "reports/training",
          label: "Training Reports",
          icon: (
            <Icon className="-ml-0.5 text-2xl" component={Icons.ReportIcon} />
          ),
        },
      ],
    },
    ...(permissions.can("Incidents", "view")
      ? [
          {
            key: "incidents",
            label: "Incidents",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ProblemIcon}
              />
            ),
            children: [
              {
                key: "incidents/all",
                label: "All",
                icon: (
                  <Icon
                    className="-ml-0.5 text-2xl"
                    component={Icons.SummarizeIcon}
                  />
                ),
              },
            ],
          },
        ]
      : []),
    ...(permissions.can("Notifications", "view") ||
    permissions.can("NotificationsRecipients", "view")
      ? [
          {
            key: "notifications",
            label: "Notifications",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.NotificationsMultipleIcon}
              />
            ),
            children: [
              ...(permissions.can("Notifications", "view")
                ? [
                    {
                      key: "notifications/all",
                      label: "All",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.StackedEmailIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("NotificationsRecipients", "view")
                ? [
                    {
                      key: "notifications/receipts",
                      label: "Receipts",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ReceiptIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("Templates", "view") ||
    permissions.can("TemplatesCategories", "view") ||
    permissions.can("TemplatesVariables", "view") ||
    permissions.can("TemplatesEvents", "view")
      ? [
          {
            key: "templates",
            label: "Templates",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ContextualTokenIcon}
              />
            ),
            children: [
              ...(permissions.can("Templates", "view")
                ? [
                    {
                      key: "templates/all",
                      label: "All",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.StacksIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("TemplatesCategories", "view")
                ? [
                    {
                      key: "templates/categories",
                      label: "Categories",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.CategoryIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("TemplatesVariables", "view")
                ? [
                    {
                      key: "templates/variables",
                      label: "Variables",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TableViewIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("TemplatesEvents", "view")
                ? [
                    {
                      key: "templates/triggers",
                      label: "Triggers",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.AdsClickIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("Processes", "view") ||
    permissions.can("ProcessesAssignment", "view")
      ? [
          {
            key: "processes",
            label: "Processes",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ManufacturingIcon}
              />
            ),
            children: [
              ...(permissions.can("Processes", "view")
                ? [
                    {
                      key: "processes/roadmaps",
                      label: "Roadmaps",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ViewWeekIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("ProcessesAssignment", "view")
                ? [
                    {
                      key: "processes/assignments",
                      label: "Assignments",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TaskIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("WellbeingCorner", "view") ||
    permissions.can("WellbeingCornerCategories", "view") ||
    permissions.can("WellbeingCornerAssignment", "view") ||
    permissions.can("WellbeingCornerEngagement", "view")
      ? [
          {
            key: "wellbeing-corner",
            label: "WellBeing Corner",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.CollectionsBookmarkIcon}
              />
            ),
            children: [
              ...(permissions.can("WellbeingCorner", "view")
                ? [
                    {
                      key: "wellbeing-corner/library",
                      label: "Library",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.DocsIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("WellbeingCornerCategories", "view")
                ? [
                    {
                      key: "wellbeing-corner/categories",
                      label: "Categories",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.CategoryIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("WellbeingCornerAssignment", "view")
                ? [
                    {
                      key: "wellbeing-corner/assignments",
                      label: "Assignments",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.AdsClickIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("WellbeingCornerEngagement", "view")
                ? [
                    {
                      key: "wellbeing-corner/engagement",
                      label: "Engagement",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.MonitoringIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("Invoices", "view")
      ? [
          {
            key: "invoices",
            label: "Invoices",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ReceipLongIcon}
              />
            ),
          },
        ]
      : []),
    ...(permissions.can("Permissions", "view") ||
    permissions.can("CompanyDomains", "view") ||
    permissions.can("CompanyRolesFlags", "view") ||
    permissions.can("CalendarEventsTypes", "view") ||
    permissions.can("IncidentTypes", "view") ||
    permissions.can("AssetTypes", "view") ||
    permissions.can("AssetSubtypes", "view") ||
    permissions.can("Languages", "view") ||
    permissions.can("CompanyProgramCategories", "view") ||
    isAppAdmin
      ? [
          {
            key: "configuration",
            label: "Configuration",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ManageAccountsIcon}
              />
            ),
            children: [
              ...(permissions.can("Permissions", "view")
                ? [
                    {
                      key: "configuration/permissions",
                      label: "Permissions",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.PasskeyIcon}
                        />
                      ),
                    },
                    {
                      key: "configuration/permission-types",
                      label: "Permission Types",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.LockPersonIcon}
                        />
                      ),
                    },
                  ]
                : []),

              ...(permissions.can("CompanyDomains", "view")
                ? [
                    {
                      key: "configuration/industries",
                      label: "Industries",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.Graph6Icon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("Labels", "view")
                ? [
                    {
                      key: "configuration/labels",
                      label: "Labels",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.LabelIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("CalendarEventsTypes", "view")
                ? [
                    {
                      key: "configuration/event-types",
                      label: "Event Types",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.EventListIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("IncidentTypes", "view")
                ? [
                    {
                      key: "configuration/incident-types",
                      label: "Incident Types",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.ReportIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("AssetTypes", "view")
                ? [
                    {
                      key: "configuration/asset-types",
                      label: "Asset Types",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.CategoryIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("AssetSubtypes", "view")
                ? [
                    {
                      key: "configuration/asset-subtypes",
                      label: "Asset Subtypes",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.CategoryIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("Languages", "view") || isAppAdmin
                ? [
                    {
                      key: "configuration/languages",
                      label: "Languages",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.LabelIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("CompanyProgramCategories", "view") || isAppAdmin
                ? [
                    {
                      key: "configuration/program-categories",
                      label: "Program Categories",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.CategoryIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(permissions.can("AppAudits", "view") ||
    permissions.can("ActionLogs", "view")
      ? [
          {
            key: "system-log",
            label: "System Log",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.TvSigninIcon}
              />
            ),
            children: [
              ...(permissions.can("ActionLogs", "view")
                ? [
                    {
                      key: "system-log/action-logs",
                      label: "Action Logs",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.PendingActionsIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("AppAudits", "view")
                ? [
                    {
                      key: "system-log/data-logs",
                      // PD-01 §21 — alias so admins find it by either label.
                      label: "Data / Lifecycle Logs",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.Assignment2Icon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(isAppAdmin
                ? [
                    {
                      key: "system-log/login-logs",
                      label: "Login Logs",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.TvSigninIcon}
                        />
                      ),
                    },
                  ]
                : []),
              ...(permissions.can("NotificationsRecipients", "view")
                ? [
                    {
                      key: "system-log/message-logs",
                      label: "Message Logs",
                      icon: (
                        <Icon
                          className="-ml-0.5 text-2xl"
                          component={Icons.MarkEmailReadIcon}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  return <SiderMenu selectedKey={key} items={items} onSelect={onSelectItem} />;
}

export default NavigationMenu;
